import React, { Fragment, useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual, omit, pick } from 'lodash';
import { ICONS } from 'Components/utils/icon/icons';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import {
  ACTIONS,
  useCriteriaDisplayContext,
} from 'Components/widgets/criteria/CriteriaDisplayContext';
import { CriteriaTrigger } from 'Components/widgets/criteria/CriteriaTrigger';
import type { ButtonProps } from 'Components/frameworks/button/Button';
import { OPEN_CRITERIA_WHO_EVENT } from 'Components/widgets/criteria/criteria-who/constants';
import { FilterWho } from 'Components/widgets/criteria/criteria-who/filter-who/FilterWho';
import type { ComponentProps } from 'react';
import { asWidgetProps } from 'Src/types/widget-helpers';
import {
  NOT_VALIDATED,
  IS_INVALID,
} from 'Components/frameworks/form-validator-hoc/validationStates';
import { t } from 'Src/i18n';
import { changeKidsNumber, setKidsValidationState } from 'Store/faceting/facetingSearch.action';
import { DEFAULT_ADULTS_NB } from 'Store/trip-information/adults/constants';
import { setAdultsNumber } from 'Store/trip-information/adults/adults.action';
import { setKids } from 'Store/trip-information/kids/kids.action';
import { getAdultsNumber, getKids } from 'Store/trip-information/storeHelper';
import {
  getValidationStateFromKid,
  getValidationStateFromKids,
  hasOnlyValidKids,
} from 'Utils/faceting/kidsValidationStateUtils';
import { useTracking } from 'Tracking/tracking-api/useTracking';
type Props = {
  disableKids?: boolean;
  hasChildcareSwitch?: boolean;
  isKidBirthdateRequired?: boolean;
  isJoyview?: boolean;
};
const KIDS_ACTIONS = {
  CHANGE_KID: 'CHANGE_KID',
  CHANGE_KIDS_COUNT: 'CHANGE_KIDS_COUNT',
  REMOVE_KIDS: 'REMOVE_KIDS',
  SYNC_KIDS: 'SYNC_KIDS',
};

const getKidBirthdateAndChildcare = (kid) => pick(kid, ['birthdate', 'childcareIncluded']);

const getKidWithoutStatus = (kid) => omit(kid, ['status']);

const getKidWithStatus = (kid, isKidBirthdateRequired) => ({
  ...kid,
  status: getValidationStateFromKid(kid, isKidBirthdateRequired),
});

export const CriteriaWho = (props: Props) => {
  const { disableKids, hasChildcareSwitch = true, isKidBirthdateRequired, isJoyview } = props;
  const [criteriaContext, setCriteriaContext] = useContext(CriteriaContext);
  const [criteriaDisplayContext, dispatchCriteriaDisplayContext] = useCriteriaDisplayContext();
  const dispatch = useDispatch();
  const { isAppMounted } = useResponsive();
  const adultsNumber = useSelector(getAdultsNumber);
  const kids = useSelector(getKids);
  const track = useTracking({
    actionGA: criteriaContext.trackingActionName,
  });
  const [inputAdultsNumber, setInputAdultsNumber] = useState(adultsNumber || DEFAULT_ADULTS_NB);
  const [inputKids, setInputKids] = useReducer((state, action) => {
    switch (action.type) {
      case KIDS_ACTIONS.SYNC_KIDS:
        return action.payload;

      case KIDS_ACTIONS.REMOVE_KIDS:
        return [];

      case KIDS_ACTIONS.CHANGE_KIDS_COUNT:
        return action.payload > state.length
          ? [
              ...state,
              {
                birthdate: undefined,
                childcareIncluded: false,
                neolid: undefined,
                status: NOT_VALIDATED,
              },
            ]
          : state.slice(0, -1);

      case KIDS_ACTIONS.CHANGE_KID:
        return state.map((kid, index) =>
          index === action.payload.index
            ? getKidWithStatus(action.payload.kid, isKidBirthdateRequired)
            : kid,
        );

      default:
        return state;
    }
  }, kids.map(getKidWithStatus));
  const inputKidsValidationState = inputKids.map((kid) => kid.status);
  const hasInvalidKidsStatus = inputKidsValidationState.some(
    (status) => status === NOT_VALIDATED || status === IS_INVALID,
  );
  const inputKidsEqualKids = isEqual(
    inputKids.map(getKidBirthdateAndChildcare),
    kids.map(getKidBirthdateAndChildcare),
  );
  const inputAdultsNumberEqualsAdultsNumber = inputAdultsNumber === adultsNumber;
  // Store update effect
  useEffect(() => {
    const adultsAreSet = adultsNumber > 0;
    const kidsAreSet = hasOnlyValidKids(getValidationStateFromKids(kids)); // Is also true when there are no kids

    setCriteriaContext({
      criteriaWhoIsSet: adultsAreSet && kidsAreSet,
    });
  }, [adultsNumber, kids]);

  const openCriteriaEventHandler = () => {
    dispatchCriteriaDisplayContext({
      type: ACTIONS.OPEN_CRITERIA,
      payload: 'criteriaWho',
    });
  };

  // Open/Close effect
  useEffect(() => {
    document.addEventListener(OPEN_CRITERIA_WHO_EVENT, openCriteriaEventHandler);
    return () => document.removeEventListener(OPEN_CRITERIA_WHO_EVENT, openCriteriaEventHandler);
  }, []);

  const saveParticipants = () => {
    if (disableKids) {
      criteriaContext.setFacetHasSeeMore(false);
      dispatch(setKids([]));
      dispatch(setKidsValidationState([]));
      dispatch(changeKidsNumber(0));
      setInputKids({
        type: KIDS_ACTIONS.SYNC_KIDS,
        payload: [],
      });
    }

    if (!disableKids && !inputKidsEqualKids) {
      criteriaContext.setFacetHasSeeMore(false);
      dispatch(setKids(inputKids.map(getKidWithoutStatus)));
      dispatch(setKidsValidationState(inputKidsValidationState));

      if (kids.length !== inputKids.length) {
        dispatch(changeKidsNumber(inputKids.length));
      }
    }

    if (!disableKids && inputKidsEqualKids) {
      // Sync kids store with inputKids
      setInputKids({
        type: KIDS_ACTIONS.SYNC_KIDS,
        payload: inputKids,
      });
    }

    if (inputKids.length > 0 && inputAdultsNumber === 0) {
      dispatch(setAdultsNumber(DEFAULT_ADULTS_NB));
    } else if (!inputAdultsNumberEqualsAdultsNumber) {
      dispatch(setAdultsNumber(inputAdultsNumber));
    }

    dispatchCriteriaDisplayContext({
      type: ACTIONS.CLOSE_CRITERIA,
      payload: 'criteriaWho',
    });
  };

  let labelParts = '';
  let labelPartsMobile = '';

  if (isAppMounted && adultsNumber > 0) {
    const labelPartsTmp = [];
    const lengthPeople = adultsNumber + kids.length;

    if (lengthPeople) {
      labelPartsMobile = lengthPeople.toString(10);

      if (adultsNumber >= 1) {
        labelPartsTmp.push(
          t('booking.form.selectAdultsLabels', {
            count: adultsNumber,
          }),
        );
      }

      if (kids.length >= 1) {
        labelPartsTmp.push(
          t('booking.form.selectChildrenLabels', {
            count: kids.length,
          }),
        );
      }

      labelParts = labelPartsTmp.join(' - ');
    }
  } else {
    labelParts = t('faceting.who.title');
  }

  const buttonLabel = (
    <Fragment>
      <span className="hidden truncate lg:inline">{labelParts}</span>
      <span className="lg:hidden">{labelPartsMobile}</span>
    </Fragment>
  );

  const trackKidsNumberChange = (value: number) => {
    if (criteriaContext.trackingActionName) {
      track({
        labelGA: 'who_child',
        valueGA: value,
      });
    }
  };

  const trackAdultsNumberChange = (value: number) => {
    if (criteriaContext.trackingActionName) {
      track({
        labelGA: 'who_adult',
        valueGA: value,
      });
    }
  };

  const onOpen = useCallback(() => {
    dispatchCriteriaDisplayContext({
      type: ACTIONS.OPEN_CRITERIA,
      payload: 'criteriaWho',
    });
  }, []);
  const onClear = useCallback(() => {
    setCriteriaContext({
      criteriaWhoIsSet: false,
    });
    dispatch(setAdultsNumber(0));
    dispatch(setKids([]));
    dispatch(changeKidsNumber(0));
    dispatch(setKidsValidationState([]));
    setInputKids({
      type: KIDS_ACTIONS.REMOVE_KIDS,
    });
    setInputAdultsNumber(DEFAULT_ADULTS_NB);
  }, []);
  return (
    <CriteriaTrigger
      buttonLabel={buttonLabel}
      buttonIcon={ICONS.PEOPLE_DOUBLE}
      buttonIsFilled={criteriaContext.criteriaWhoIsSet}
      dataTestId="CriteriaWho"
      dropDownStyle={{
        minWidth: 500,
      }}
      isOpen={criteriaDisplayContext.criteriaWhoIsOpen}
      isJoyview={isJoyview}
      trackingLabel="who"
      onOpen={onOpen}
      onClose={saveParticipants}
      onClear={onClear}
      buttons={[
        {
          children: t('faceting.reset'),
          'data-testid': 'criteria-who-reset',
          onClick: () => {
            setInputAdultsNumber(DEFAULT_ADULTS_NB);
            setInputKids({
              type: KIDS_ACTIONS.REMOVE_KIDS,
            });
          },
          theme: 'link',
        } as ButtonProps & { 'data-testid'?: string },
        {
          children: t('faceting.validate'),
          'data-testid': 'criteria-who-validate',
          isDisabled: isKidBirthdateRequired && hasInvalidKidsStatus,
          onClick: () => {
            saveParticipants();
            dispatchCriteriaDisplayContext({
              type: ACTIONS.OPEN_NEXT_CRITERIA,
              payload: {
                currentCriteria: 'criteriaWho',
                criteriaContext,
              },
            });
          },
        } as ButtonProps & { 'data-testid'?: string },
      ]}
    >
      <FilterWho
        {...asWidgetProps<ComponentProps<typeof FilterWho>>({
          adultsLabel: t('faceting.who.adults'),
          adultsNumber: inputAdultsNumber,
          disableKids,
          hasChildcareSwitch,
          kids: inputKids,
          placeholder: t('faceting.who.title'),
          label: t('faceting.who.subTitle'),
          kidsLabel: t('faceting.who.kids'),
          kidsAgeLabel: isKidBirthdateRequired
            ? t('faceting.who.ageSectionSubtitle')
            : t('faceting.who.ageSectionChildcareSubtitle'),
          kidsAgeLimitLabel: t('faceting.who.kidsAgeLimit'),
          dateFormat: t('booking.form.dateFormat'),
          dayPlaceholder: t('booking.form.dayPlaceholder'),
          monthPlaceholder: t('booking.form.monthPlaceholder'),
          yearPlaceholder: t('booking.form.yearPlaceholder'),
          informationLabel: !isKidBirthdateRequired ? t('faceting.who.ageComment') : undefined,
          addChildcareLabel: t('faceting.who.addChildcare'),
          birthdaysErrorMessage: isKidBirthdateRequired
            ? t('booking.form.errors.birthdayFormat')
            : '',
          changeKidsNumber: (value) => {
            setInputKids({
              type: KIDS_ACTIONS.CHANGE_KIDS_COUNT,
              payload: value,
            });
            trackKidsNumberChange(value);
          },
          changeKid: (index, kid) => {
            setInputKids({
              type: KIDS_ACTIONS.CHANGE_KID,
              payload: {
                index,
                kid,
              },
            });
          },
          kidsValidationState: inputKidsValidationState,
          validateKidsBirthdate: (index: number, kid) => {
            setInputKids({
              type: KIDS_ACTIONS.CHANGE_KID,
              payload: {
                index,
                kid,
              },
            });
          },
          setAdultsNumber: (number) => {
            setInputAdultsNumber(number);
            trackAdultsNumberChange(number);
          },
        })}
      />
    </CriteriaTrigger>
  );
};
